import React, { useState, useEffect } from "react";
import Header from "./Header";
import Drawer from "./Drawer";
import "./dashboard.css";
import "./mainContent.css";
import { usePermissions } from "../contexts/PermissionContext";
import MemberDetailsPage from "./Pages/MemberDetails";
import { Route, Routes, useLocation } from "react-router-dom";
import Dashboard from "./Pages/Dashboard";
import EnhancedDashboard from "./Pages/Dashboard/EnhancedDashboard";
import Timesheets from "./Pages/Timesheet";
import Members from "./Pages/Member";
import AddWorkSchedule from "./Pages/AddWorkSchedule";
import AddWorkLocation from "./Pages/AddWorkLocation";
import LocationTracking from "./Pages/LocationTracking";
import TimeEntries from "./Pages/TimeEntries";
import AppUsage from "./Pages/AppUsage";
import ApprovalTimeEntries from "./Pages/ApprovalTimeEntries";
import Footer from "./Pages/Footer";
import AppMonitering from "./Pages/AppMonitering";
import WorkAssignment from "./Pages/WorkAssignment";
import Reports from "./Pages/Report";
import Subscription from "./Pages/Subscription";
import AccountSetting from "./Pages/AccountSettings";
import SubscriptionHistory from "./Pages/subscriptionHistory";
import PaymentSuccess from "./Pages/PaymentSuccess";
import PaymentFailure from "./Pages/PaymentFailure";
import Nearby from "./Pages/Nearby";
import {
  SuperAdminDashboard,
  OrganizationList,
  OrganizationDetails,
  CreateOrganization,
  SubscriptionPlans,
  AuditLogs as SuperAdminAuditLogs,
  SuperAdminUsers,
  RolePermissionManagement
} from "./Pages/SuperAdmin";
import SubOrgManager from "./Pages/Organization/SubOrgManager";
import ChildOrganizations from "./Pages/ChildOrganizations";
import SuspensionBanner from "./SuspensionBanner";
import OrgAuditLogs from "./Pages/AuditLogs";


const Sidebar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isTabletExpanded, setIsTabletExpanded] = useState(false);
  const [isTabletMode, setIsTabletMode] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const location = useLocation();
  const { loading: permissionsLoading } = usePermissions();

  // Fade out overlay once permissions finish loading
  useEffect(() => {
    if (!permissionsLoading) {
      // Brief delay so the fade transition is visible
      const t = setTimeout(() => setShowOverlay(false), 350);
      return () => clearTimeout(t);
    }
  }, [permissionsLoading]);

  // Check if we're in tablet mode (768px-991px)
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsTabletMode(width >= 768 && width <= 991);

      // Reset states when leaving tablet mode
      if (width > 991) {
        setIsTabletExpanded(false);
        setIsMobileMenuOpen(false);
      }
      // Reset tablet expanded when going to mobile
      if (width < 768) {
        setIsTabletExpanded(false);
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsTabletExpanded(false);
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    if (isTabletMode) {
      // Toggle tablet expanded state
      setIsTabletExpanded(!isTabletExpanded);
    } else {
      // Toggle mobile menu
      setIsMobileMenuOpen(!isMobileMenuOpen);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsTabletExpanded(false);
  };

  return (
    <>
      {/* Full-screen permission loading overlay */}
      {(permissionsLoading || showOverlay) && (
        <div className={`permission-loader-overlay${!permissionsLoading ? ' fade-out' : ''}`}>
          <div className="permission-loader-spinner" />
          <span className="permission-loader-text">Loading...</span>
        </div>
      )}

      <div className="row p-0 m-0 no-scrollbar">
        <div
          className="sticky-header col-12 p-0"
          style={{ position: "sticky", top: 0, zIndex: 1000 }}
        >
          <Header onMenuToggle={toggleMobileMenu} isMobileMenuOpen={isMobileMenuOpen} isTabletExpanded={isTabletExpanded} />
          <SuspensionBanner />
        </div>

        {/* Mobile Overlay */}
        {isMobileMenuOpen && (
          <div
            className="sidebar-overlay show"
            onClick={closeMobileMenu}
          />
        )}

        {/* Tablet Overlay (when sidebar is expanded) */}
        {isTabletExpanded && isTabletMode && (
          <div
            className="sidebar-overlay tablet-show"
            onClick={closeMobileMenu}
          />
        )}

        {/* Sidebar Drawer */}
        <div className={`sidebar-drawer pt-3 p-0 bg-color ${isMobileMenuOpen ? 'open' : ''} ${isTabletExpanded ? 'expanded' : ''}`}>
          <Drawer onItemClick={closeMobileMenu} />
        </div>

        {/* Main Content */}
        <div
          className="main-content p-0 m-0"
          style={{
            overflowY: "scroll",
            height: "calc(100vh - 88px)",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/analytics" element={<EnhancedDashboard />} />
            <Route path="/timesheets" element={<Timesheets />} />
            <Route path="/member-details" element={<MemberDetailsPage />} />
            <Route path="/members" element={<Members />} />
            <Route path="/addworkschedule" element={<AddWorkSchedule />} />
            <Route path="/addWorkLocation" element={<AddWorkLocation />} />
            <Route path="/locationTracking" element={<LocationTracking />} />
            <Route path="/timeentries" element={<TimeEntries />} />
            <Route path="/appusage" element={<AppUsage />} />
            <Route path="/approvaltimeentries" element={<ApprovalTimeEntries />}/>
            <Route path="/monitoring" element={<AppMonitering />} />
            <Route path="/workassignments" element={<WorkAssignment />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/subscription" element={<Subscription />} />
            <Route path="/accountsetting" element={<AccountSetting />} />
            <Route
              path="/subscriptionHistory"
              element={<SubscriptionHistory />}
            />
            <Route path="/paymentsuccess" element={<PaymentSuccess />} />
            <Route path="/paymentfailure" element={<PaymentFailure />} />
            <Route path="/nearby" element={<Nearby />} />

            {/* Organization Management Routes */}
            <Route path="/sub-organizations" element={<SubOrgManager />} />
            <Route path="/child-organizations" element={<ChildOrganizations />} />

            {/* Audit Logs Route (For All Users) */}
            <Route path="/audit-logs" element={<OrgAuditLogs />} />

            {/* Super Admin Routes */}
            <Route path="/super-admin" element={<SuperAdminDashboard />} />
            <Route path="/super-admin/organizations" element={<OrganizationList />} />
            <Route path="/super-admin/organizations/create" element={<CreateOrganization />} />
            <Route path="/super-admin/organizations/:orgId" element={<OrganizationDetails />} />
            <Route path="/super-admin/plans" element={<SubscriptionPlans />} />
            <Route path="/super-admin/audit-logs" element={<SuperAdminAuditLogs />} />
            <Route path="/super-admin/users" element={<SuperAdminUsers />} />
            <Route path="/super-admin/role-permissions" element={<RolePermissionManagement />} />
          </Routes>
        </div>
        <div
          className="col-12 p-0 bg-color"
          style={{ position: "sticky", bottom: 0, zIndex: 1000 }}
        >
          <Footer />
        </div>
      </div>
    </>
  );
};

export default Sidebar;
